import React, { useState, useEffect } from 'react';
import { UserAccount, UserRole } from '../types';
import {
  getUserAccounts,
  upsertUserAccount,
  deleteUserAccount,
  toggleUserStatus,
  resetUserPassword,
  adminSetUserPasswordAndFlags,
  ALL_RESTRICTED_AREAS,
} from '../utils/auth';
import BackupManagement from './communication/BackupManagement';
import { AdminMultimediaManager } from './admin/AdminMultimediaManager';
import AdminVillagesManager from './admin/AdminVillagesManager';
import { DataHealthReportDashboard } from './admin/DataHealthReportDashboard';
import { GlobalOverridesPanel } from './admin/GlobalOverridesPanel';
import { FinancialManagementModule } from './admin/FinancialManagementModule';
import { AdminAnalyticsDashboard } from './admin/AdminAnalyticsDashboard';
import { OrgManagementPanel } from './admin/OrgManagementPanel';
import { getAuditLogs, AuditLogEntry } from '../utils/auditStorage';

interface AdminUserManagementModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentUser: UserAccount | null;
  onUserAccountsUpdated?: () => void;
}

export const AdminUserManagementModal: React.FC<AdminUserManagementModalProps> = ({
  isOpen,
  onClose,
  currentUser,
  onUserAccountsUpdated,
}) => {
  if (!isOpen) return null;

  if (!currentUser || currentUser.role !== 'admin') {
    return (
      <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/80 backdrop-blur-md p-4">
        <div className="bg-slate-900 border border-red-500/50 rounded-3xl p-8 max-w-md w-full text-center space-y-4 shadow-2xl">
          <div className="w-16 h-16 bg-red-500/10 border border-red-500/30 rounded-full flex items-center justify-center mx-auto text-3xl">
            ⛔
          </div>
          <h3 className="text-xl font-serif font-black text-red-300">Administrator Clearance Required</h3>
          <p className="text-xs text-gray-300 leading-relaxed">
            Only authorized Global Administrators (<span className="text-amber-300 font-mono">role: admin</span>) are permitted to manage users, website settings, edit global content, and perform administrative operations.
          </p>
          <button
            onClick={onClose}
            className="w-full py-2.5 bg-slate-800 hover:bg-slate-700 text-amber-300 rounded-xl font-bold text-xs cursor-pointer border border-slate-700"
          >
            Close / Return to Portal
          </button>
        </div>
      </div>
    );
  }

  const [activeAdminTab, setActiveAdminTab] = useState<'users' | 'multimedia' | 'villages' | 'health' | 'overrides' | 'audit_recovery' | 'finance' | 'audit_logs' | 'analytics' | 'org_management'>('users');
  const [accounts, setAccounts] = useState<UserAccount[]>(() => getUserAccounts());
  const [auditLogs, setAuditLogs] = useState<AuditLogEntry[]>(() => getAuditLogs());
  const [auditCategoryFilter, setAuditCategoryFilter] = useState<string>('all');
  const [auditSearch, setAuditSearch] = useState('');

  useEffect(() => {
    const handleAuditUpdate = () => {
      setAuditLogs(getAuditLogs());
    };
    window.addEventListener('uzairue_audit_logged', handleAuditUpdate);
    return () => {
      window.removeEventListener('uzairue_audit_logged', handleAuditUpdate);
    };
  }, []);

  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState<string>('all');
  const [statusFilter, setStatusFilter] = useState<string>('all');

  // Form state for creating / editing users
  const [isFormOpen, setIsFormOpen] = useState(false);
  const [editingUserId, setEditingUserId] = useState<string | null>(null);
  const [formData, setFormData] = useState<{
    email: string;
    name: string;
    title: string;
    role: UserRole;
    avatar: string;
    department: string;
    password?: string;
    mustChangePassword: boolean;
    allowedRestrictedAreas: string[];
    forcePasswordResetReason: string;
  }>({
    email: '',
    name: '',
    title: '',
    role: 'writer',
    avatar: '✍️',
    department: 'Secretariat Bureau',
    password: 'uzairue2026',
    mustChangePassword: true,
    allowedRestrictedAreas: ['publishing-suite', 'communication-portal', 'resources-investment'],
    forcePasswordResetReason: 'Initial user account provisioning by global administrator.',
  });

  // Advanced Password & Access Control Modal State
  const [resetModalUserId, setResetModalUserId] = useState<string | null>(null);
  const [newPasswordInput, setNewPasswordInput] = useState('pass2026');
  const [modalMustChangePassword, setModalMustChangePassword] = useState(true);
  const [modalReason, setModalReason] = useState('Administrative security policy reset requirement.');
  const [modalRestrictedAreas, setModalRestrictedAreas] = useState<string[]>([]);

  if (!isOpen) return null;

  const refreshAccounts = () => {
    const fresh = getUserAccounts();
    setAccounts(fresh);
    if (onUserAccountsUpdated) onUserAccountsUpdated();
  };

  const filteredAccounts = accounts.filter((acc) => {
    const matchesQuery =
      acc.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
      acc.department.toLowerCase().includes(searchQuery.toLowerCase());
    const matchesRole = roleFilter === 'all' || acc.role === roleFilter;
    const matchesStatus =
      statusFilter === 'all' ||
      (statusFilter === 'active' && acc.status !== 'suspended') ||
      (statusFilter === 'suspended' && acc.status === 'suspended');
    return matchesQuery && matchesRole && matchesStatus;
  });

  const handleOpenAddForm = () => {
    setEditingUserId(null);
    setFormData({
      email: '',
      name: '',
      title: '',
      role: 'writer',
      avatar: '✍️',
      department: 'Secretariat Bureau',
      password: 'uzairue2026',
      mustChangePassword: true,
      allowedRestrictedAreas: ['publishing-suite', 'communication-portal', 'resources-investment'],
      forcePasswordResetReason: 'Initial user account provisioning by global administrator.',
    });
    setIsFormOpen(true);
  };

  const handleOpenEditForm = (acc: UserAccount) => {
    setEditingUserId(acc.id);
    setFormData({
      email: acc.email,
      name: acc.name,
      title: acc.title,
      role: acc.role,
      avatar: acc.avatar,
      department: acc.department,
      password: acc.password || '',
      mustChangePassword: acc.mustChangePassword ?? false,
      allowedRestrictedAreas: acc.allowedRestrictedAreas || ['publishing-suite', 'communication-portal', 'resources-investment'],
      forcePasswordResetReason: acc.forcePasswordResetReason || '',
    });
    setIsFormOpen(true);
  };

  const handleToggleArea = (areaId: string, currentList: string[], setter: (list: string[]) => void) => {
    if (currentList.includes(areaId)) {
      setter(currentList.filter((a) => a !== areaId));
    } else {
      setter([...currentList, areaId]);
    }
  };

  const handleSaveUser = (e: React.FormEvent) => {
    e.preventDefault();
    if (!formData.email || !formData.name) {
      alert('Email and Name are required.');
      return;
    }

    upsertUserAccount({
      id: editingUserId || undefined,
      email: formData.email,
      name: formData.name,
      title: formData.title,
      role: formData.role,
      avatar: formData.avatar,
      department: formData.department,
      password: formData.password || 'uzairue2026',
      mustChangePassword: formData.mustChangePassword,
      allowedRestrictedAreas: formData.allowedRestrictedAreas,
      forcePasswordResetReason: formData.forcePasswordResetReason,
    });

    setIsFormOpen(false);
    refreshAccounts();
  };

  const handleDelete = (userId: string, userName: string) => {
    if (userId === currentUser?.id) {
      alert('⚠️ Security Protection: You cannot delete your own active admin account.');
      return;
    }
    if (confirm(`Are you sure you want to permanently delete account for "${userName}"?`)) {
      deleteUserAccount(userId);
      refreshAccounts();
    }
  };

  const handleToggleStatus = (userId: string, userName: string) => {
    if (userId === currentUser?.id) {
      alert('⚠️ Security Protection: You cannot suspend your own active admin account.');
      return;
    }
    toggleUserStatus(userId);
    refreshAccounts();
  };

  const handleOpenPasswordAndAccessModal = (acc: UserAccount) => {
    setResetModalUserId(acc.id);
    setNewPasswordInput(acc.password || 'uzairue2026');
    setModalMustChangePassword(acc.mustChangePassword ?? true);
    setModalReason(acc.forcePasswordResetReason || 'Global administrator requested password change.');
    setModalRestrictedAreas(acc.allowedRestrictedAreas || ['publishing-suite', 'communication-portal', 'resources-investment']);
  };

  const handleGenerateRandomPassword = () => {
    const chars = 'ABCDEFGHJKLMNPQRSTUVWXYZabcdefghijkmnopqrstuvwxyz23456789#$@';
    let generated = 'Uzairue@';
    for (let i = 0; i < 6; i++) {
      generated += chars.charAt(Math.floor(Math.random() * chars.length));
    }
    setNewPasswordInput(generated);
  };

  const handleExecutePasswordResetAndAccess = (userId: string) => {
    if (!newPasswordInput || newPasswordInput.trim().length < 4) {
      alert('Password must be at least 4 characters long.');
      return;
    }
    adminSetUserPasswordAndFlags(
      userId,
      newPasswordInput.trim(),
      modalMustChangePassword,
      modalRestrictedAreas,
      modalReason
    );
    setResetModalUserId(null);
    refreshAccounts();
    alert('✅ Password & Restricted Access Permissions Updated Successfully!');
  };

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/70 backdrop-blur-xs p-4 overflow-y-auto">
      <div className="bg-white rounded-2xl max-w-4xl w-full shadow-2xl border border-amber-400/40 overflow-hidden my-6">
        {/* Header */}
        <div className="bg-gradient-to-r from-[#0F5132] via-[#08331f] to-slate-900 text-white p-5 flex items-center justify-between border-b-2 border-amber-400">
          <div className="flex items-center gap-3">
            <span className="text-3xl">🏛️</span>
            <div>
              <h2 className="text-lg font-serif font-black tracking-wide text-amber-300">
                Uzairue Royal Secretariat — User & Password Management Console
              </h2>
              <p className="text-xs text-emerald-200">
                Full User Setup • Password Reset Enforcement • Restricted Area Permissions • Account Suspensions
              </p>
            </div>
          </div>

          <button
            onClick={onClose}
            className="w-8 h-8 rounded-full bg-white/10 hover:bg-white/20 text-white flex items-center justify-center text-lg font-bold transition-all cursor-pointer"
          >
            ✕
          </button>
        </div>

        {/* Content Body */}
        <div className="p-6 space-y-6 max-h-[78vh] overflow-y-auto bg-slate-50">
          {/* Top Admin Tab Navigation */}
          <div className="flex flex-wrap border-b border-gray-200 gap-2 pb-2">
            <button
              onClick={() => setActiveAdminTab('users')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'users'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>👥</span> User Accounts & Passwords
            </button>

            <button
              onClick={() => setActiveAdminTab('multimedia')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'multimedia'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>📸</span> Multimedia & Asset Deployer
            </button>

            <button
              onClick={() => setActiveAdminTab('villages')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'villages'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>🏛️</span> 22 Villages & Communities
            </button>

            <button
              onClick={() => setActiveAdminTab('health')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'health'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>📊</span> Data Health Report
            </button>

            <button
              onClick={() => setActiveAdminTab('overrides')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'overrides'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>⚙️</span> General Settings
            </button>

            <button
              onClick={() => setActiveAdminTab('audit_recovery')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'audit_recovery'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>🛡️</span> Audit & Recovery
            </button>

            <button
              onClick={() => setActiveAdminTab('finance')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'finance'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>💰</span> Financial Management
            </button>

            <button
              onClick={() => setActiveAdminTab('audit_logs')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'audit_logs'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>📋</span> Audit Logs
            </button>

            <button
              onClick={() => setActiveAdminTab('analytics')}
              className={`px-4 py-2 rounded-xl text-xs font-black uppercase tracking-wider transition-all cursor-pointer flex items-center gap-2 ${
                activeAdminTab === 'analytics'
                  ? 'bg-[#0F5132] text-amber-300 shadow-md'
                  : 'bg-white text-gray-700 hover:bg-gray-100 border border-gray-200'
              }`}
            >
              <span>📊</span> Analytics Dashboard
            </button>
              <button
                onClick={() => setActiveAdminTab('org_management')}
                className={`px-3 py-2 rounded-xl text-xs font-bold cursor-pointer transition-all ${
                  activeAdminTab === 'org_management'
                    ? 'bg-amber-500 text-slate-900'
                    : 'text-gray-400 hover:text-white hover:bg-slate-700'
                }`}
              >
                🏛️ Organisation
              </button>
          </div>

          {activeAdminTab === 'multimedia' ? (
            <AdminMultimediaManager
              adminEmail={currentUser?.email || 'erueuzairue@gmail.com'}
              adminName={currentUser?.name || 'Ikpemhi Oshioke'}
            />
          ) : activeAdminTab === 'villages' ? (
            <AdminVillagesManager
              adminEmail={currentUser?.email || 'erueuzairue@gmail.com'}
              adminName={currentUser?.name || 'Ikpemhi Oshioke'}
            />
          ) : activeAdminTab === 'health' ? (
            <DataHealthReportDashboard />
          ) : activeAdminTab === 'overrides' ? (
            <GlobalOverridesPanel
              adminEmail={currentUser?.email || 'erueuzairue@gmail.com'}
              adminName={currentUser?.name || 'Ikpemhi Oshioke'}
            />
          ) : activeAdminTab === 'audit_recovery' ? (
            <div className="bg-slate-950 p-6 rounded-2xl border border-amber-400/40">
              <BackupManagement />
            </div>
          ) : activeAdminTab === 'finance' ? (
             <FinancialManagementModule />
          ) : activeAdminTab === 'analytics' ? (
             <AdminAnalyticsDashboard />
          ) : activeAdminTab === 'audit_logs' ? (
            <div className="bg-white p-6 rounded-3xl border border-gray-200 shadow-sm space-y-6">
              <div className="flex flex-col md:flex-row justify-between items-start md:items-center gap-4 border-b border-gray-100 pb-4">
                <div>
                  <h3 className="text-lg font-serif font-black text-[#0F5132]">Institutional Audit Logs & Governance Compliance</h3>
                  <p className="text-xs text-gray-500">Comprehensive real-time tracking of user activities, document sealing, and financial modifications.</p>
                </div>
                <button
                  onClick={() => {
                    const printWin = window.open('', '_blank');
                    if (!printWin) return;
                    printWin.document.write(`
                      <html>
                        <head>
                          <title>UZAIRUE KINGDOM INSTITUTIONAL AUDIT LOG REPORT</title>
                          <style>
                            body { font-family: 'Times New Roman', serif; margin: 30px; color: #111; }
                            h1 { font-size: 20px; color: #0F5132; text-align: center; border-bottom: 2px solid #0F5132; padding-bottom: 10px; }
                            table { width: 100%; border-collapse: collapse; margin-top: 20px; font-size: 11px; }
                            th, td { border: 1px solid #ccc; padding: 6px 8px; text-align: left; }
                            th { background: #0F5132; color: #fff; }
                          </style>
                        </head>
                        <body>
                          <h1>UZAIRUE KINGDOM - OFFICIAL INSTITUTIONAL AUDIT LOG REPORT</h1>
                          <p>Generated: ${new Date().toLocaleString()} by Administrator (${currentUser?.name})</p>
                          <table>
                            <thead>
                              <tr>
                                <th>Timestamp</th>
                                <th>Actor Name</th>
                                <th>Role</th>
                                <th>Category</th>
                                <th>Action Description</th>
                                <th>Details</th>
                              </tr>
                            </thead>
                            <tbody>
                              ${auditLogs.map(l => `
                                <tr>
                                  <td>${l.timestamp}</td>
                                  <td>${l.userName}</td>
                                  <td>${l.userRole}</td>
                                  <td>${l.category.toUpperCase()}</td>
                                  <td>${l.action}</td>
                                  <td>${l.details}</td>
                                </tr>
                              `).join('')}
                            </tbody>
                          </table>
                        </body>
                      </html>
                    `);
                    printWin.document.close();
                    printWin.print();
                  }}
                  className="px-4 py-2 bg-[#0F5132] hover:bg-[#07301d] text-amber-300 rounded-xl text-xs font-bold shadow-md cursor-pointer flex items-center gap-1.5"
                >
                  <span>🖨️</span> Export & Print Audit Report
                </button>
              </div>

              <div className="flex flex-col md:flex-row gap-3 items-center justify-between">
                <input
                  type="text"
                  placeholder="Search audit logs by action, actor, or details..."
                  value={auditSearch}
                  onChange={(e) => setAuditSearch(e.target.value)}
                  className="w-full md:w-80 px-3 py-2 border border-gray-300 rounded-xl text-xs focus:ring-2 focus:ring-[#0F5132]"
                />
                <select
                  value={auditCategoryFilter}
                  onChange={(e) => setAuditCategoryFilter(e.target.value)}
                  className="px-3 py-2 border border-gray-300 rounded-xl text-xs bg-white font-medium focus:ring-2 focus:ring-[#0F5132]"
                >
                  <option value="all">All Categories</option>
                  <option value="authentication">Authentication</option>
                  <option value="document">Document</option>
                  <option value="financial">Financial</option>
                  <option value="raffle">Raffle</option>
                  <option value="admin">Admin</option>
                  <option value="system">System</option>
                </select>
              </div>

              <div className="overflow-x-auto max-h-96 overflow-y-auto">
                <table className="w-full text-xs text-left">
                  <thead className="bg-gray-100 text-gray-700 font-bold uppercase text-[10px] sticky top-0">
                    <tr>
                      <th className="p-3">Timestamp</th>
                      <th className="p-3">Actor / User</th>
                      <th className="p-3">Role</th>
                      <th className="p-3">Category</th>
                      <th className="p-3">Action</th>
                      <th className="p-3">Details</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-gray-100">
                    {auditLogs
                      .filter(l => {
                        const matchesSearch = l.action.toLowerCase().includes(auditSearch.toLowerCase()) ||
                          l.userName.toLowerCase().includes(auditSearch.toLowerCase()) ||
                          l.details.toLowerCase().includes(auditSearch.toLowerCase());
                        const matchesCategory = auditCategoryFilter === 'all' || l.category === auditCategoryFilter;
                        return matchesSearch && matchesCategory;
                      })
                      .map(log => (
                        <tr key={log.id} className="hover:bg-gray-50">
                          <td className="p-3 font-mono text-gray-500 whitespace-nowrap">{log.timestamp}</td>
                          <td className="p-3 font-bold text-gray-900">{log.userName}</td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-slate-100 text-slate-800">
                              {log.userRole}
                            </span>
                          </td>
                          <td className="p-3">
                            <span className="px-2 py-0.5 rounded-full text-[10px] font-bold uppercase bg-amber-100 text-amber-900">
                              {log.category}
                            </span>
                          </td>
                          <td className="p-3 font-semibold text-gray-900">{log.action}</td>
                          <td className="p-3 text-gray-600 max-w-xs truncate" title={log.details}>{log.details}</td>
                        </tr>
                      ))}
                  </tbody>
                </table>
              </div>
            </div>
          ) : (
            <>
              {/* Controls Bar: Search + Filters + Add User Button */}
              <div className="bg-white p-4 rounded-xl border border-gray-200 shadow-2xs flex flex-col md:flex-row md:items-center justify-between gap-3">
            <div className="flex-1 flex flex-wrap items-center gap-2">
              <div className="relative flex-1 min-w-[200px]">
                <span className="absolute left-3 top-2.5 text-gray-400 text-xs">🔍</span>
                <input
                  type="text"
                  placeholder="Search users by name, email, department..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="w-full pl-8 pr-3 py-2 border border-gray-300 rounded-lg text-xs focus:ring-2 focus:ring-[#0F5132] focus:outline-none"
                />
              </div>

              <select
                value={roleFilter}
                onChange={(e) => setRoleFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white font-medium focus:outline-none"
              >
                <option value="all">All Roles</option>
                <option value="writer">Level 1: Writer / Scribe</option>
                <option value="authorizer">Level 2: Authorizer / Ruler</option>
                <option value="admin">Level 3: Administrator</option>
              </select>

              <select
                value={statusFilter}
                onChange={(e) => setStatusFilter(e.target.value)}
                className="px-3 py-2 border border-gray-300 rounded-lg text-xs bg-white font-medium focus:outline-none"
              >
                <option value="all">All Statuses</option>
                <option value="active">Active Only</option>
                <option value="suspended">Suspended Only</option>
              </select>
            </div>

            <button
              onClick={handleOpenAddForm}
              className="px-4 py-2 bg-[#0F5132] hover:bg-[#08331f] text-amber-300 rounded-xl text-xs font-extrabold flex items-center gap-2 transition-all cursor-pointer shadow-xs border border-amber-400/50 shrink-0"
            >
              <span>➕ Add New User Account</span>
            </button>
          </div>

          {/* User Accounts Table */}
          <div className="bg-white rounded-xl border border-gray-200 shadow-2xs overflow-hidden">
            <div className="p-3 bg-gray-100 border-b border-gray-200 flex items-center justify-between">
              <span className="text-xs font-bold text-gray-700">
                Registered Secretariat Accounts ({filteredAccounts.length})
              </span>
              <span className="text-[10px] text-gray-500 font-mono">
                RBAC Level Active: {currentUser?.role.toUpperCase()}
              </span>
            </div>

            <div className="divide-y divide-gray-100">
              {filteredAccounts.length === 0 ? (
                <div className="p-8 text-center text-gray-500">
                  <p className="text-sm font-semibold">No user accounts found matching query.</p>
                </div>
              ) : (
                filteredAccounts.map((acc) => {
                  const isSuspended = acc.status === 'suspended';
                  const needsReset = acc.mustChangePassword || !acc.initialLoginCompleted;
                  const areasCount = (acc.role === 'admin' ? ALL_RESTRICTED_AREAS : (acc.allowedRestrictedAreas || [])).length;

                  return (
                    <div
                      key={acc.id}
                      className={`p-4 flex flex-col md:flex-row md:items-center justify-between gap-4 transition-colors ${
                        isSuspended ? 'bg-red-50/50' : 'hover:bg-amber-50/30'
                      }`}
                    >
                      {/* User Info */}
                      <div className="flex items-start gap-3 flex-1 min-w-0">
                        <span className="text-2xl p-2 bg-gray-100 rounded-xl border border-gray-200 shrink-0">
                          {acc.avatar}
                        </span>
                        <div className="min-w-0 flex-1 space-y-1">
                          <div className="flex items-center gap-2 flex-wrap">
                            <h3 className="text-sm font-extrabold text-gray-900 truncate">{acc.name}</h3>
                            <span
                              className={`text-[9px] font-extrabold px-2 py-0.5 rounded-full border ${
                                acc.role === 'admin'
                                  ? 'bg-purple-100 text-purple-900 border-purple-300'
                                  : acc.role === 'authorizer'
                                  ? 'bg-amber-100 text-amber-900 border-amber-300'
                                  : 'bg-emerald-100 text-emerald-900 border-emerald-300'
                              }`}
                            >
                              {acc.role === 'admin'
                                ? '🏛️ Admin'
                                : acc.role === 'authorizer'
                                ? '👑 Authorizer'
                                : '✍️ Writer'}
                            </span>

                            {needsReset ? (
                              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-amber-100 text-amber-900 border border-amber-300 flex items-center gap-1">
                                <span>🔑</span> Reset Req
                              </span>
                            ) : (
                              <span className="text-[9px] font-bold uppercase px-1.5 py-0.5 rounded bg-emerald-50 text-emerald-800 border border-emerald-200">
                                ✓ Setup Complete
                              </span>
                            )}

                            {isSuspended ? (
                              <span className="text-[9px] font-black uppercase px-2 py-0.5 rounded bg-red-600 text-white animate-pulse">
                                🚫 Suspended
                              </span>
                            ) : (
                              <span className="text-[9px] font-extrabold uppercase px-2 py-0.5 rounded bg-emerald-600 text-white">
                                ✓ Active
                              </span>
                            )}
                          </div>

                          <p className="text-xs text-gray-600 font-medium">{acc.title}</p>
                          
                          <div className="flex items-center gap-3 text-[10px] text-gray-500 flex-wrap font-mono">
                            <span>📧 {acc.email}</span>
                            <span>🔑 Password: <strong className="text-amber-900">{acc.password || '••••••••'}</strong></span>
                            <span>🏢 {acc.department}</span>
                          </div>

                          {/* Restricted Areas Pills */}
                          <div className="flex items-center gap-1 flex-wrap pt-0.5">
                            <span className="text-[9px] font-bold text-gray-500">Restricted Areas ({areasCount}):</span>
                            {(acc.role === 'admin'
                              ? ALL_RESTRICTED_AREAS.map((a) => a.id)
                              : acc.allowedRestrictedAreas || []
                            ).map((areaId) => {
                              const found = ALL_RESTRICTED_AREAS.find((a) => a.id === areaId);
                              return (
                                <span
                                  key={areaId}
                                  className="text-[8px] font-extrabold px-1.5 py-0.5 rounded bg-slate-100 text-slate-800 border border-slate-200"
                                >
                                  {found ? `${found.icon} ${found.name}` : areaId}
                                </span>
                              );
                            })}
                          </div>
                        </div>
                      </div>

                      {/* Action Buttons */}
                      <div className="flex items-center gap-1.5 shrink-0 flex-wrap">
                        <button
                          onClick={() => handleOpenEditForm(acc)}
                          className="px-2.5 py-1.5 bg-gray-100 hover:bg-gray-200 text-gray-800 rounded-lg text-xs font-bold border border-gray-300 transition-all cursor-pointer"
                          title="Edit User Profile"
                        >
                          ✏️ Edit
                        </button>

                        <button
                          onClick={() => handleOpenPasswordAndAccessModal(acc)}
                          className="px-2.5 py-1.5 bg-amber-100 hover:bg-amber-200 text-amber-900 rounded-lg text-xs font-bold border border-amber-300 transition-all cursor-pointer flex items-center gap-1"
                          title="Manage User Password & Restricted Access Permissions"
                        >
                          <span>🔑</span> Password & Access
                        </button>

                        <button
                          onClick={() => handleToggleStatus(acc.id, acc.name)}
                          className={`px-2.5 py-1.5 rounded-lg text-xs font-bold border transition-all cursor-pointer ${
                            isSuspended
                              ? 'bg-emerald-600 hover:bg-emerald-700 text-white border-emerald-700'
                              : 'bg-orange-600 hover:bg-orange-700 text-white border-orange-700'
                          }`}
                          title={isSuspended ? 'Reactivate Account' : 'Suspend Account'}
                        >
                          {isSuspended ? '🔓 Reactivate' : '🚫 Suspend'}
                        </button>

                        <button
                          onClick={() => handleDelete(acc.id, acc.name)}
                          className="px-2.5 py-1.5 bg-red-100 hover:bg-red-200 text-red-700 rounded-lg text-xs font-bold border border-red-300 transition-all cursor-pointer"
                          title="Delete Account"
                        >
                          🗑️ Delete
                        </button>
                      </div>
                    </div>
                  );
                })
              )}
            </div>
          </div>
          </>
          )}
        </div>
      </div>

      {/* Add / Edit User Modal */}
      {isFormOpen && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <form
            onSubmit={handleSaveUser}
            className="bg-white rounded-2xl max-w-lg w-full p-6 space-y-4 shadow-2xl border border-amber-400 my-8"
          >
            <div className="flex justify-between items-center border-b pb-3 border-gray-200">
              <h3 className="text-base font-extrabold text-[#0F5132] flex items-center gap-2">
                <span>{editingUserId ? '✏️ Edit Secretariat Account' : '➕ Provision New User Account'}</span>
              </h3>
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="text-gray-400 hover:text-gray-600 text-lg font-bold cursor-pointer"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3 text-xs max-h-[65vh] overflow-y-auto pr-1">
              <div>
                <label className="font-bold text-gray-700 block mb-1">Full Name & Title</label>
                <input
                  type="text"
                  required
                  placeholder="e.g., High Chief Kingsley"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F5132]"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Official Email Address</label>
                <input
                  type="email"
                  required
                  placeholder="e.g., name@erueuzairue.org"
                  value={formData.email}
                  onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0F5132]"
                />
              </div>

              <div className="grid grid-cols-2 gap-2">
                <div>
                  <label className="font-bold text-gray-700 block mb-1">Role & Authority Level</label>
                  <select
                    value={formData.role}
                    onChange={(e) => setFormData({ ...formData, role: e.target.value as UserRole })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg font-bold bg-white"
                  >
                    <option value="writer">Level 1: Writer / Scribe</option>
                    <option value="authorizer">Level 2: Authorizer / Ruler</option>
                    <option value="admin">Level 3: Administrator</option>
                  </select>
                </div>

                <div>
                  <label className="font-bold text-gray-700 block mb-1">Avatar Emoji</label>
                  <input
                    type="text"
                    placeholder="e.g. ✍️, 👑, 🏛️, 📜"
                    value={formData.avatar}
                    onChange={(e) => setFormData({ ...formData, avatar: e.target.value })}
                    className="w-full p-2.5 border border-gray-300 rounded-lg text-center font-bold"
                  />
                </div>
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Official Designation Title</label>
                <input
                  type="text"
                  placeholder="e.g. Chief Protocol Officer"
                  value={formData.title}
                  onChange={(e) => setFormData({ ...formData, title: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Secretariat Department</label>
                <input
                  type="text"
                  placeholder="e.g. Cultural & Heritage Bureau"
                  value={formData.department}
                  onChange={(e) => setFormData({ ...formData, department: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg"
                />
              </div>

              <div>
                <label className="font-bold text-gray-700 block mb-1">Initial Account Password</label>
                <input
                  type="text"
                  required
                  placeholder="Password"
                  value={formData.password}
                  onChange={(e) => setFormData({ ...formData, password: e.target.value })}
                  className="w-full p-2.5 border border-gray-300 rounded-lg font-mono text-xs"
                />
              </div>

              {/* Password Reset Flag & Controls */}
              <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer font-bold text-amber-900">
                  <input
                    type="checkbox"
                    checked={formData.mustChangePassword}
                    onChange={(e) => setFormData({ ...formData, mustChangePassword: e.target.checked })}
                    className="w-4 h-4 text-[#0F5132] rounded focus:ring-[#0F5132]"
                  />
                  <span>🔑 Require User to Change Password Upon Next Login</span>
                </label>
                <p className="text-[10px] text-amber-800">
                  When checked, the user will be redirected to the Password Reset screen immediately upon authenticating.
                </p>

                {formData.mustChangePassword && (
                  <div>
                    <label className="font-bold text-gray-700 block mb-1 text-[10px]">
                      Reason for Forced Password Reset
                    </label>
                    <input
                      type="text"
                      value={formData.forcePasswordResetReason}
                      onChange={(e) => setFormData({ ...formData, forcePasswordResetReason: e.target.value })}
                      placeholder="e.g. Initial account provisioning security mandate"
                      className="w-full p-2 border border-amber-300 rounded text-xs bg-white"
                    />
                  </div>
                )}
              </div>

              {/* Allowed Restricted Areas Checklist */}
              <div className="space-y-2 pt-1 border-t border-gray-200">
                <label className="font-extrabold text-gray-800 uppercase tracking-wider block">
                  🛡️ Restricted Area Permissions Clearance:
                </label>
                <div className="space-y-1.5">
                  {ALL_RESTRICTED_AREAS.map((area) => {
                    const isChecked = formData.role === 'admin' || formData.allowedRestrictedAreas.includes(area.id);
                    const isDisabled = formData.role === 'admin'; // Admins automatically get all
                    return (
                      <label
                        key={area.id}
                        className={`flex items-start gap-2 p-2 rounded-lg border cursor-pointer ${
                          isChecked ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          disabled={isDisabled}
                          checked={isChecked}
                          onChange={() => handleToggleArea(area.id, formData.allowedRestrictedAreas, (list) => setFormData({ ...formData, allowedRestrictedAreas: list }))}
                          className="mt-0.5 w-4 h-4 text-[#0F5132] rounded"
                        />
                        <div>
                          <span className="font-bold text-gray-900 flex items-center gap-1">
                            <span>{area.icon}</span>
                            <span>{area.name}</span>
                          </span>
                          <p className="text-[10px] text-gray-500">{area.description}</p>
                        </div>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <button
                type="button"
                onClick={() => setIsFormOpen(false)}
                className="px-4 py-2 border rounded-xl text-xs font-bold hover:bg-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                type="submit"
                className="px-5 py-2 bg-[#0F5132] text-amber-300 rounded-xl text-xs font-extrabold hover:bg-[#08331f] cursor-pointer"
              >
                Save User Account
              </button>
            </div>
          </form>
        </div>
      )}

      {/* Advanced Password & Access Management Modal */}
      {resetModalUserId && (
        <div className="fixed inset-0 z-60 flex items-center justify-center bg-black/60 p-4 overflow-y-auto">
          <div className="bg-white rounded-2xl max-w-md w-full p-5 space-y-4 shadow-2xl border border-amber-400 my-8">
            <div className="flex items-center justify-between border-b pb-2 border-gray-200">
              <div className="flex items-center gap-2">
                <span className="text-xl">🔑</span>
                <h3 className="text-sm font-extrabold text-[#0F5132]">
                  Password & Access Permissions Setup
                </h3>
              </div>
              <button
                onClick={() => setResetModalUserId(null)}
                className="text-gray-400 hover:text-gray-600 font-bold"
              >
                ✕
              </button>
            </div>

            <div className="space-y-3.5 text-xs">
              <div>
                <div className="flex items-center justify-between mb-1">
                  <label className="font-bold text-gray-700 uppercase tracking-wider">
                    New User Password
                  </label>
                  <button
                    type="button"
                    onClick={handleGenerateRandomPassword}
                    className="text-[10px] text-[#0F5132] hover:underline font-extrabold"
                  >
                    ⚡ Generate Strong Password
                  </button>
                </div>
                <input
                  type="text"
                  value={newPasswordInput}
                  onChange={(e) => setNewPasswordInput(e.target.value)}
                  className="w-full p-2.5 border rounded-lg text-xs font-mono font-bold border-gray-300"
                  placeholder="Enter new password"
                />
              </div>

              <div className="bg-amber-50 p-3 rounded-xl border border-amber-200 space-y-2">
                <label className="flex items-center gap-2 cursor-pointer font-bold text-amber-900">
                  <input
                    type="checkbox"
                    checked={modalMustChangePassword}
                    onChange={(e) => setModalMustChangePassword(e.target.checked)}
                    className="w-4 h-4 text-[#0F5132] rounded"
                  />
                  <span>Require User to Reset Password Upon Next Login</span>
                </label>

                {modalMustChangePassword && (
                  <div>
                    <label className="font-bold text-gray-700 block mb-1 text-[10px]">
                      Reason / Message for User
                    </label>
                    <input
                      type="text"
                      value={modalReason}
                      onChange={(e) => setModalReason(e.target.value)}
                      placeholder="e.g. Administrative security update requirement"
                      className="w-full p-2 border rounded text-xs bg-white"
                    />
                  </div>
                )}
              </div>

              {/* Exclusive Restricted Areas Checklist */}
              <div className="space-y-1.5 pt-1 border-t border-gray-200">
                <label className="font-extrabold text-gray-800 uppercase tracking-wider block">
                  🛡️ Restricted Area Access Clearances:
                </label>
                <div className="space-y-1">
                  {ALL_RESTRICTED_AREAS.map((area) => {
                    const isChecked = modalRestrictedAreas.includes(area.id);
                    return (
                      <label
                        key={area.id}
                        className={`flex items-center gap-2 p-2 rounded-lg border cursor-pointer ${
                          isChecked ? 'bg-emerald-50 border-emerald-300' : 'bg-gray-50 border-gray-200'
                        }`}
                      >
                        <input
                          type="checkbox"
                          checked={isChecked}
                          onChange={() => handleToggleArea(area.id, modalRestrictedAreas, setModalRestrictedAreas)}
                          className="w-4 h-4 text-[#0F5132] rounded"
                        />
                        <span className="font-bold text-gray-900 flex items-center gap-1">
                          <span>{area.icon}</span>
                          <span>{area.name}</span>
                        </span>
                      </label>
                    );
                  })}
                </div>
              </div>
            </div>

            <div className="flex justify-end gap-2 pt-3 border-t border-gray-200">
              <button
                onClick={() => setResetModalUserId(null)}
                className="px-3 py-2 border rounded-xl text-xs font-bold hover:bg-gray-100 cursor-pointer"
              >
                Cancel
              </button>
              <button
                onClick={() => handleExecutePasswordResetAndAccess(resetModalUserId)}
                className="px-4 py-2 bg-[#0F5132] text-amber-300 rounded-xl text-xs font-bold hover:bg-[#08331f] cursor-pointer shadow-md"
              >
                Save Credentials & Access Permissions 🔑
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

